import React, { ReactNode } from 'react';

import { Box, Paper, Typography } from '@mui/material';
import Masonry from '@mui/lab/Masonry';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import { RouterOutput } from '../../api/trpc';

import { Row } from '../../atoms/row';

import { Battery } from '../../atoms/icons/battery';
import { Boiler } from '../../atoms/icons/boiler';
import { HeatPump } from '../../atoms/icons/heat-pump';
import { Wallbox } from '../../atoms/icons/wallbox';
import { Statistics } from '../../atoms/icons/statistics';
import { SolarPanel } from '../../atoms/icons/solar-panel';
import { Zap } from '../../atoms/icons/zap';
import { useFormatNumber } from '../../utils/format-number';
import { colors } from '../../theme/palette';
import { ReportAvgDayChart } from '../../organisms/reports/report-avg-day-chart';
import { ReportGenerateButton } from '../../organisms/reports/report-generate-button';
import { ReportRfidCard } from '../../organisms/reports/report-rfid-card';
import { Card, defaultKwh } from '../../organisms/reports/report-card';
import { useSelector } from '../../store/utils';
import { notNullish } from '../../utils/utils';

// Drops conditionally-omitted (null) rows while keeping each surviving entry
// typed as a fixed [label, value] tuple — a plain inline .filter() widens the
// tuples to arrays and breaks assignment to Card's `lines` prop.
const cardLines = (
  lines: ([ReactNode, ReactNode] | null)[]
): [ReactNode, ReactNode][] => lines.filter(notNullish);

type Detail = RouterOutput['reports']['get'];

type Props = {
  report: Detail;
  month: string;
};

export const ReportDetail = ({ report, month }: Props) => {
  const formatNumber = useFormatNumber();

  const [pvBattery, hwInstalled, hpEnabled, wbInstalled] = useSelector(
    ({ variables: { PV_BATTERY, HW_INSTALL, HP_ENABLED, WB_INSTALL } }) => [
      PV_BATTERY,
      HW_INSTALL,
      HP_ENABLED,
      WB_INSTALL,
    ]
  );

  return (
    <Box sx={{ mx: { xs: 0, md: '-8px !important' } }}>
      <Masonry columns={{ xs: 1, md: 2 }} spacing={0}>
        <Card
          title={
            <FormattedMessage id="reports.info" defaultMessage="Obecné info" />
          }
          icon={<Zap />}
          pieChart={[
            {
              label: (
                <FormattedMessage
                  id="reports.pv_consumption"
                  defaultMessage="Spotřeba z FVE"
                  key={0}
                />
              ),
              labelValue: formatNumber(
                report.pvProduction - report.emOverflow,
                defaultKwh
              ),
              value: report.pvProduction - report.emOverflow,
              color: colors.primary,
            },
            {
              label: (
                <FormattedMessage
                  id="reports.grid_consumption"
                  defaultMessage="Odběr ze sítě"
                  key={0}
                />
              ),
              labelValue: formatNumber(report.emConsumed, defaultKwh),
              value: report.emConsumed,
              color: colors.darkBlue,
            },
          ]}
          lines={[
            [
              <FormattedMessage
                id="reports.pv_production"
                defaultMessage="Výroba FVE"
                key={0}
              />,
              formatNumber(report.pvProduction, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.total_overflow_to_grid"
                defaultMessage="Celkový přetok do sítě"
                key={0}
              />,
              formatNumber(report.emOverflow, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.total_purchase_from_grid"
                defaultMessage="Celkový nákup ze sítě"
                key={0}
              />,
              formatNumber(report.emConsumed, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.building_consumption"
                defaultMessage="Spotřeba objektu"
                key={0}
              />,
              formatNumber(report.homeConsumed, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.self_consumption"
                defaultMessage="Vlastní využití energie"
                key={0}
              />,
              formatNumber(report.pvUsed * 100, {
                style: 'percent',
                maximumFractionDigits: 0,
              }),
            ],
            [
              <FormattedMessage
                id="reports.self_sufficiency"
                defaultMessage="Soběstačnost"
                key={0}
              />,
              formatNumber(report.selfSufficiency * 100, {
                style: 'percent',
                maximumFractionDigits: 0,
              }),
            ],
            [
              <FormattedMessage
                id="reports.grid_consumption"
                defaultMessage="Odběr ze sítě"
                key={0}
              />,
              formatNumber(report.fromGrid * 100, {
                style: 'percent',
                maximumFractionDigits: 0,
              }),
            ],
            [
              <FormattedMessage
                id="reports.self_sufficient_days"
                defaultMessage="Počet soběstačních dní"
                key={0}
              />,
              <>
                {report.selfSufficiencyDays} / {report.selfSufficiencyDaysTotal}
              </>,
            ],
          ]}
        />

        <Card
          title={
            <FormattedMessage
              id="reports.avg_day"
              defaultMessage="Průmerný den"
            />
          }
          content={<ReportAvgDayChart data={report.avgDayChart} />}
        />

        <Card
          title={
            <FormattedMessage
              id="reports.pv"
              defaultMessage="Fotovoltaická elektrárna"
            />
          }
          visibility={{ variableId: 'PV_INSTALL' }}
          icon={<SolarPanel />}
          lines={[
            [
              <FormattedMessage
                id="reports.monthly_production"
                defaultMessage="Měsíční výroba"
                key={0}
              />,
              formatNumber(report.pvProduction, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.maximum_power"
                defaultMessage="Maximální výkon"
                key={0}
              />,
              formatNumber(report.pvMaxPower, defaultKwh),
            ],
          ]}
        />

        <Card
          title={
            <FormattedMessage id="reports.boiler" defaultMessage="Bojler" />
          }
          icon={<Boiler />}
          visibility={{ variableId: 'HW_INSTALL' }}
          pieChart={[
            {
              label: (
                <FormattedMessage
                  id="reports.pv_consumption"
                  defaultMessage="Spotřeba z FVE"
                  key={0}
                />
              ),
              labelValue: formatNumber(report.hwConsumedFromPv, defaultKwh),
              value: report.hwConsumedFromPv,
              color: colors.primary,
            },
            {
              label: (
                <FormattedMessage
                  id="reports.grid_consumption_hw"
                  defaultMessage="Spotřeba ze sítě"
                  key={0}
                />
              ),
              labelValue: formatNumber(report.hwConsumedFromGrid, defaultKwh),
              value: report.hwConsumedFromGrid,
              color: colors.darkBlue,
            },
          ]}
          lines={[
            [
              <FormattedMessage
                id="reports.monthly_consumption"
                defaultMessage="Měsíční spotřeba"
                key={0}
              />,
              formatNumber(report.hwConsumed, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.pv_consumption"
                defaultMessage="Spotřeba z FVE"
                key={1}
              />,
              formatNumber(report.hwConsumedFromPv, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.grid_consumption_hw"
                defaultMessage="Spotřeba ze sítě"
                key={2}
              />,
              formatNumber(report.hwConsumedFromGrid, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.average_water_temperature"
                defaultMessage="Průměrná teplota vody"
                key={3}
              />,
              formatNumber(report.hwAvgTemp, {
                unit: 'celsius',
                style: 'unit',
                maximumFractionDigits: 0,
              }),
            ],
            [
              <FormattedMessage
                id="reports.estimated_water_amount"
                defaultMessage="Odhadované množství vody"
                key={4}
              />,
              formatNumber(report.estimateTankVolume, {
                unit: 'litre',
                style: 'unit',
                maximumFractionDigits: 0,
              }),
            ],
          ]}
        />

        <Card
          title={
            <FormattedMessage
              id="reports.heat_pump"
              defaultMessage="Tepelné čerpadlo"
            />
          }
          icon={<HeatPump />}
          visibility={{ variableId: 'HP_ENABLED' }}
          pieChart={[
            {
              label: (
                <FormattedMessage
                  id="reports.pv_consumption"
                  defaultMessage="Spotřeba z FVE"
                  key={0}
                />
              ),
              labelValue: formatNumber(report.hpConsumedFromPv, defaultKwh),
              value: report.hpConsumedFromPv,
              color: colors.primary,
            },
            {
              label: (
                <FormattedMessage
                  id="reports.grid_consumption_hp"
                  defaultMessage="Spotřeba ze sítě"
                  key={0}
                />
              ),
              labelValue: formatNumber(report.hpConsumedFromGrid, defaultKwh),
              value: report.hpConsumedFromGrid,
              color: colors.darkBlue,
            },
          ]}
          lines={[
            [
              <FormattedMessage
                id="reports.monthly_consumption"
                defaultMessage="Měsíční spotřeba"
                key={0}
              />,
              formatNumber(report.hpConsumed, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.pv_consumption"
                defaultMessage="Spotřeba z FVE"
                key={1}
              />,
              formatNumber(report.hpConsumedFromPv, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.grid_consumption_hp"
                defaultMessage="Spotřeba ze sítě"
                key={2}
              />,
              formatNumber(report.hpConsumedFromGrid, defaultKwh),
            ],
          ]}
        />

        {/* Keep the Wallbox card and the RFID breakdown together as one
            masonry item so they stay in the same column on desktop. */}
        <Box>
          <Card
            title={
              <FormattedMessage id="reports.wallbox" defaultMessage="Wallbox" />
            }
            icon={<Wallbox />}
            visibility={{ variableId: 'WB_INSTALL' }}
            pieChart={[
              {
                label: (
                  <FormattedMessage
                    id="reports.pv_consumption"
                    defaultMessage="Spotřeba z FVE"
                    key={0}
                  />
                ),
                labelValue: formatNumber(report.wbConsumedFromPv, defaultKwh),
                value: report.wbConsumedFromPv,
                color: colors.primary,
              },
              {
                label: (
                  <FormattedMessage
                    id="reports.grid_consumption_wallbox"
                    defaultMessage="Spotřeba ze sítě"
                    key={0}
                  />
                ),
                labelValue: formatNumber(report.wbConsumedFromGrid, defaultKwh),
                value: report.wbConsumedFromGrid,
                color: colors.darkBlue,
              },
            ]}
            lines={[
              [
                <FormattedMessage
                  id="reports.monthly_consumption"
                  defaultMessage="Měsíční spotřeba"
                  key={0}
                />,
                formatNumber(report.wbConsumed, defaultKwh),
              ],
              [
                <FormattedMessage
                  id="reports.pv_consumption"
                  defaultMessage="Spotřeba z FVE"
                  key={1}
                />,
                formatNumber(report.wbConsumedFromPv, defaultKwh),
              ],
              [
                <FormattedMessage
                  id="reports.grid_consumption_wallbox"
                  defaultMessage="Spotřeba ze sítě"
                  key={2}
                />,
                formatNumber(report.wbConsumedFromGrid, defaultKwh),
              ],
              [
                <FormattedMessage
                  id="reports.kilometers_driven"
                  defaultMessage="Počet ujetých km"
                  key={3}
                />,
                formatNumber(report.wbKm, {
                  unit: 'kilometer',
                  style: 'unit',
                  maximumFractionDigits: 0,
                }),
              ],
              [
                <FormattedMessage
                  id="reports.kilometers_driven_from_sun"
                  defaultMessage="Počet ujetých km ze slunce"
                  key={4}
                />,
                formatNumber(report.wbKmFromPv, {
                  unit: 'kilometer',
                  style: 'unit',
                  maximumFractionDigits: 0,
                }),
              ],
            ]}
          />

          <ReportRfidCard consumptionByCard={report.wbConsumedByCard} />
        </Box>

        <Card
          title={
            <FormattedMessage id="reports.battery" defaultMessage="Baterie" />
          }
          visibility={{ variableId: 'PV_BATTERY' }}
          icon={<Battery />}
          lines={[
            [
              <FormattedMessage
                id="reports.stored_energy_from_pv"
                defaultMessage="Uložená energie z FVE"
                key={0}
              />,
              formatNumber(report.chargedFromPv, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.stored_energy_from_grid"
                defaultMessage="Uložená energie ze sítě"
                key={1}
              />,
              formatNumber(report.chargedFromGrid, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.household_consumption"
                defaultMessage="Spotřeba domácnosti"
                key={2}
              />,
              formatNumber(report.dischargedToHome, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.sold_to_distribution_grid"
                defaultMessage="Prodáno do distribuční sítě"
                key={3}
              />,
              formatNumber(report.dischargedToGrid, defaultKwh),
            ],
          ]}
        />

        <Card
          title={
            <FormattedMessage
              id="reports.energy_estimate"
              defaultMessage="Odhad energií bez řízení"
            />
          }
          icon={<Statistics />}
          lines={cardLines([
            pvBattery
              ? [
                  <FormattedMessage
                    id="reports.stored_energy_to_battery"
                    defaultMessage="Uložená energie do baterie"
                    key={0}
                  />,
                  formatNumber(
                    report.withoutControlBatteryAccumulation,
                    defaultKwh
                  ),
                ]
              : null,
            hwInstalled
              ? [
                  <FormattedMessage
                    id="reports.stored_energy_to_boiler"
                    defaultMessage="Uložená energie do bojleru"
                    key={1}
                  />,
                  formatNumber(
                    report.withoutControlBoilerAccumulation,
                    defaultKwh
                  ),
                ]
              : null,
            hpEnabled
              ? [
                  <FormattedMessage
                    id="reports.stored_energy_to_heat_pump"
                    defaultMessage="Uložená energie do TČ"
                    key={2}
                  />,
                  formatNumber(
                    report.withoutControlHeatPumpAccumulation,
                    defaultKwh
                  ),
                ]
              : null,
            wbInstalled
              ? [
                  <FormattedMessage
                    id="reports.stored_energy_to_wallbox"
                    defaultMessage="Uložená energie do wallboxu"
                    key={3}
                  />,
                  formatNumber(
                    report.withoutControlWallboxAccumulation,
                    defaultKwh
                  ),
                ]
              : null,
            [
              <FormattedMessage
                id="reports.total_without_control"
                defaultMessage="Celkem bez řízení"
                key={4}
              />,
              formatNumber(report.withoutControlTotal, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.total_with_control"
                defaultMessage="Celkem s řízením"
                key={5}
              />,
              formatNumber(report.withControlTotal, defaultKwh),
            ],
            [
              <FormattedMessage
                id="reports.accumulation_increased"
                defaultMessage="Zvýšení akumulace"
                key={6}
              />,
              formatNumber((report.accumulationIncreased - 1) * 100, {
                style: 'percent',
                maximumFractionDigits: 0,
              }),
            ],
          ])}
        />

        <Box sx={{ p: { xs: 0, md: 1 }, pb: { xs: 2, md: 0 } }}>
          <Paper>
            <Row sx={{ gap: 0.5 }}>
              <Typography>
                <FormattedMessage
                  id="reports.updated_at"
                  defaultMessage="Vygenerováno {date}"
                  values={{
                    date: (
                      <>
                        <FormattedDate value={report.at} />{' '}
                        <FormattedTime value={report.at} />
                      </>
                    ),
                  }}
                />
              </Typography>

              <ReportGenerateButton month={month} />
            </Row>
          </Paper>
        </Box>

        <Box sx={{ p: { xs: 0, md: 1 }, pb: { xs: 2, md: 0 } }}>
          <Paper>
            <Row sx={{ gap: 0.5, opacity: 0.7 }}>
              <Typography>
                <FormattedMessage
                  id="reports.disclaimer"
                  defaultMessage="Uvedené hodnoty mají pouze informativní charakter a mohou se lišit od oficiálních odečtů distributora."
                />
              </Typography>
            </Row>
          </Paper>
        </Box>
      </Masonry>
    </Box>
  );
};
